import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { 
  Gem, 
  ChevronLeft, 
  ChevronRight, 
  Sparkles, 
  Heart, 
  Droplets,
  RotateCcw,
  Star,
  Share2,
  Copy,
  Check
} from "lucide-react";
import { LithaeLogo, LithaeIcon } from "@/components/LithaeLogo";
import NewsletterSection from "@/components/NewsletterSection";
import { STONES } from "@/data/stones";
import { recommend } from "@/utils/recommend";

const STORAGE_KEY = "quiz_profile";

// Share component
const ShareSection = ({ topStone }) => {
  const [copied, setCopied] = useState(false);
  
  const shareUrl = 'https://lithae.com/quiz';
  const stoneName = topStone?.stone?.name || 'ma pierre';
  const shareText = `J'ai découvert ma pierre idéale grâce au quiz Lithae : ${stoneName} ! ✨💎 Découvrez la vôtre :`;
  
  const handleShare = (platform) => {
    const encodedText = encodeURIComponent(shareText);
    const encodedUrl = encodeURIComponent(shareUrl);
    
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
      whatsapp: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
    };
    
    if (urls[platform]) {
      // Ouvrir dans un nouvel onglet au lieu d'une popup (meilleure compatibilité)
      window.open(urls[platform], '_blank', 'noopener,noreferrer');
    }
  };
  
  const handleCopyLink = async () => {
    const textToCopy = `${shareText} ${shareUrl}`;
    
    try {
      // Essayer d'abord la méthode moderne
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback: créer un élément temporaire et copier
      try {
        const textArea = document.createElement('textarea');
        textArea.value = textToCopy;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (successful) {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } else {
          // Si rien ne marche, afficher le texte dans une alerte
          window.prompt('Copiez ce texte :', textToCopy);
        }
      } catch (fallbackErr) {
        // Dernier recours: prompt
        window.prompt('Copiez ce texte :', textToCopy);
      }
    }
  };
  
  // Native share for mobile
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Ma pierre idéale - Lithae',
          text: shareText,
          url: shareUrl,
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    }
  };
  
  const hasNativeShare = typeof navigator !== 'undefined' && navigator.share;
  
  return (
    <div className="bg-card rounded-2xl border border-border p-6 sm:p-8 shadow-soft">
      <div className="text-center mb-6">
        <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-4">
          <Share2 className="w-6 h-6 text-primary" />
        </div>
        <h3 className="font-serif text-xl font-semibold text-foreground mb-2">
          Partagez votre découverte
        </h3>
        <p className="text-sm text-muted-foreground">
          Faites découvrir {stoneName} à vos proches
        </p>
      </div>
      
      <div className="flex flex-wrap justify-center gap-3">
        {/* Native share button for mobile */}
        {hasNativeShare && (
          <Button
            variant="primary"
            onClick={handleNativeShare}
            className="flex items-center gap-2"
          >
            <Share2 className="w-4 h-4" />
            Partager
          </Button>
        )}
        
        {/* Social buttons */}
        <button
          onClick={() => handleShare('facebook')}
          className="w-12 h-12 rounded-xl bg-[#1877F2] hover:bg-[#1877F2]/90 text-white flex items-center justify-center transition-all duration-300 hover:scale-105"
          title="Partager sur Facebook"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
          </svg>
        </button>
        
        <button
          onClick={() => handleShare('twitter')}
          className="w-12 h-12 rounded-xl bg-[#000000] hover:bg-[#000000]/80 text-white flex items-center justify-center transition-all duration-300 hover:scale-105"
          title="Partager sur X"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
          </svg>
        </button>
        
        <button
          onClick={() => handleShare('whatsapp')}
          className="w-12 h-12 rounded-xl bg-[#25D366] hover:bg-[#25D366]/90 text-white flex items-center justify-center transition-all duration-300 hover:scale-105"
          title="Partager sur WhatsApp"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        </button>
        
        <button
          onClick={handleCopyLink}
          className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-105 ${
            copied 
              ? 'bg-green-500 text-white' 
              : 'bg-muted hover:bg-muted/80 text-foreground'
          }`}
          title="Copier le lien"
        >
          {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
        </button>
      </div>
      
      {copied && (
        <p className="text-sm text-green-600 text-center mt-3 animate-fade-in">
          Lien copié !
        </p>
      )}
    </div>
  );
};

// Stone color mapping for card backgrounds
const stoneColorMap = {
  'amethyste': 'bg-stone-amethyst',
  'quartz-rose': 'bg-stone-quartz',
  'labradorite': 'bg-stone-lapis/50',
  'obsidienne': 'bg-stone-obsidian/10',
  'citrine': 'bg-stone-tiger',
  'lapis-lazuli': 'bg-stone-lapis',
  'oeil-de-tigre': 'bg-stone-tiger',
  'howlite': 'bg-muted'
};

// Chakra color mapping
const chakraColorMap = {
  'Racine': 'bg-chakra-root text-white',
  'Sacré': 'bg-chakra-sacral text-white',
  'Plexus Solaire': 'bg-chakra-plexus text-foreground',
  'Cœur': 'bg-chakra-heart text-white',
  'Gorge': 'bg-chakra-throat text-white',
  'Troisième Œil': 'bg-chakra-third-eye text-white',
  'Couronne': 'bg-chakra-crown text-white'
};

const StoneCard = ({ scoredStone, rank, isAlternative = false }) => {
  const navigate = useNavigate();
  const { stone, reasons } = scoredStone;
  const bgColor = stoneColorMap[stone.id] || 'bg-muted';

  return (
    <Card className={`overflow-hidden border-border shadow-soft hover:shadow-medium transition-all duration-300 ${isAlternative ? 'opacity-90' : ''}`}>
      {/* Header with stone image */}
      <div className={`${bgColor} relative`}>
        {!isAlternative && rank <= 3 && (
          <div className="absolute top-4 left-4 z-10">
            <Badge className="bg-primary text-primary-foreground shadow-sm">
              <Star className="w-3 h-3 mr-1" />
              Top {rank}
            </Badge>
          </div>
        )}
        {isAlternative && (
          <div className="absolute top-4 left-4 z-10">
            <Badge variant="secondary" className="shadow-sm">
              Alternative
            </Badge>
          </div>
        )}
        {/* Stone Image */}
        <div className="aspect-square relative overflow-hidden">
          {stone.imageUrl ? (
            <img 
              src={stone.imageUrl} 
              alt={stone.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Gem className="w-16 h-16 text-primary/30" />
            </div>
          )}
        </div>
        {/* Stone name overlay */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-background/90 to-transparent p-4 pt-12">
          <h3 className="font-serif text-2xl font-semibold text-foreground text-center">
            {stone.name}
          </h3>
          <p className="text-sm text-muted-foreground mt-1 text-center">
            {stone.couleur}
          </p>
        </div>
      </div>

      <CardContent className="p-6 space-y-6">
        {/* Why this stone */}
        <div>
          <h4 className="font-medium text-foreground flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-primary" />
            Pourquoi cette pierre
          </h4>
          <ul className="space-y-2">
            {reasons.slice(0, 3).map((reason, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                {reason}
              </li>
            ))}
          </ul>
        </div>

        <Separator />

        {/* Emotional/Spiritual benefits */}
        <div>
          <h4 className="font-medium text-foreground flex items-center gap-2 mb-3">
            <Heart className="w-4 h-4 text-primary" />
            Bienfaits émotionnels & spirituels
          </h4>
          <p className="text-sm text-muted-foreground leading-relaxed line-clamp-4">
            {stone.bienfaits_emo_spirituels}
          </p>
        </div>

        <Separator />

        {/* Care instructions */}
        <div>
          <h4 className="font-medium text-foreground flex items-center gap-2 mb-3">
            <Droplets className="w-4 h-4 text-primary" />
            Entretien
          </h4>
          <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">
            {stone.entretien}
          </p>
        </div>

        <Separator />

        {/* Chakras */}
        <div className="space-y-4">
          {/* Chakras */}
          <div>
            <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
              Chakras
            </h5>
            <div className="flex flex-wrap gap-2">
              {stone.chakras.map((chakra, index) => (
                <Badge 
                  key={index} 
                  className={`${chakraColorMap[chakra] || 'bg-muted text-foreground'} text-xs`}
                >
                  {chakra}
                </Badge>
              ))}
            </div>
          </div>
        </div>

        {/* CTA */}
        <Button 
          variant="outline" 
          className="w-full mt-4"
          onClick={() => navigate(`/pierre/${stone.id}`)}
        >
          Voir la fiche complète
          <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );
};

const Results = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let isMounted = true;
    
    const loadResults = () => {
      // Try to get results from navigation state first
      if (location.state?.results) {
        if (isMounted) {
          setResults(location.state.results);
          setLoading(false);
        }
        return;
      }

      // Fallback: calculate from localStorage
      const savedProfile = localStorage.getItem(STORAGE_KEY);
      if (savedProfile && isMounted) {
        const profile = JSON.parse(savedProfile);
        const calculatedResults = recommend(STONES, profile);
        setResults(calculatedResults);
      }
      if (isMounted) {
        setLoading(false);
      }
    };

    loadResults();
    
    return () => {
      isMounted = false;
    };
  }, [location.state]);

  const handleRestart = () => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem("quiz_answers");
    navigate('/quiz');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <LithaeIcon size={48} className="mx-auto animate-pulse" />
          <p className="mt-4 text-muted-foreground">Analyse en cours...</p>
        </div>
      </div>
    );
  }

  if (!results || results.top.length === 0) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <LithaeIcon size={64} className="mx-auto mb-6 text-muted-foreground" />
          <h1 className="font-serif text-2xl font-semibold text-foreground mb-4">
            Aucun résultat trouvé
          </h1>
          <p className="text-muted-foreground mb-8">
            Il semble que vous n&apos;ayez pas encore complété le quiz. 
            Commencez le quiz pour découvrir vos pierres idéales.
          </p>
          <Button variant="primary" onClick={() => navigate('/quiz')}>
            Commencer le quiz
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-background/80 backdrop-blur-md border-b border-border sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button 
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm">Accueil</span>
            </button>
            <div className="flex items-center gap-2">
              <LithaeIcon size={20} />
              <span className="font-serif text-lg font-medium text-foreground">
                Vos résultats
              </span>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleRestart}
              className="text-muted-foreground"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Refaire
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-12 sm:py-16 bg-gradient-hero">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-6">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary">
              Analyse complète
            </span>
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-foreground mb-4">
            Vos pierres idéales
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Basé sur vos réponses, voici les pierres qui correspondent le mieux 
            à vos besoins et à votre sensibilité.
          </p>
        </div>
      </section>

      {/* Top 3 Results */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-serif text-2xl font-semibold text-foreground mb-8 text-center">
            Nos 3 meilleures recommandations
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {results.top.map((scoredStone, index) => (
              <StoneCard 
                key={scoredStone.stone.id} 
                scoredStone={scoredStone} 
                rank={index + 1}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter & Share Section - Side by side */}
      <section className="py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Newsletter on the left */}
            <NewsletterSection />
            {/* Share on the right */}
            <ShareSection topStone={results.top[0]} />
          </div>
        </div>
      </section>

      {/* Alternatives */}
      {results.alternatives.length > 0 && (
        <section className="py-12 px-4 bg-muted/30">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-serif text-2xl font-semibold text-foreground mb-8 text-center">
              Alternatives à explorer
            </h2>
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {results.alternatives.map((scoredStone) => (
                <StoneCard 
                  key={scoredStone.stone.id} 
                  scoredStone={scoredStone}
                  isAlternative={true}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Disclaimer */}
      <section className="py-8 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-xs text-muted-foreground italic">
            Approche de bien-être, ne remplace pas un avis médical. 
            Les informations fournies sont à titre indicatif et ne constituent pas un conseil médical.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 px-4 bg-gradient-hero">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-serif text-2xl font-semibold text-foreground mb-4">
            Envie d&apos;explorer d&apos;autres possibilités ?
          </h2>
          <p className="text-muted-foreground mb-8">
            Refaites le quiz avec de nouveaux critères pour découvrir d&apos;autres pierres.
          </p>
          <Button variant="primary" size="lg" onClick={handleRestart}>
            <RotateCcw className="w-4 h-4 mr-2" />
            Refaire le quiz
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <a href="https://www.lithae.com" target="_blank" rel="noopener noreferrer">
              <LithaeLogo size="small" />
            </a>
            <p className="text-xs text-muted-foreground text-center sm:text-right">
              Approche de bien-être, ne remplace pas un avis médical.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Results;
